/**
 * Adapter for the existing villamine.com pages.
 *
 * villamine.com renders two hidden inputs on every page:
 *   <input id="hiddenDil"   value="en-us" />   site language: tr-tr | en-us | de-de
 *   <input id="hiddenDoviz" value="3" />        currency switcher: 1 USD, 2 GBP, 3 EUR, 4 TRY
 * When they are absent (demo host, other pages) the adapter returns nulls and
 * the widget falls back safely.
 *
 * Villa links follow the site locale: the visitor stays in the language they
 * chose on villamine.com. The AI conversation itself is not limited to these
 * three languages.
 */
import { VILLAMINE_ORIGIN } from "../domain/taxonomy";
import type { Currency } from "../domain/search";
import type { SiteLocale } from "../domain/villa";

export const SITE_LOCALES: SiteLocale[] = ["tr-tr", "en-us", "de-de"];

const CURRENCY_BY_ID: Record<string, Currency> = { "1": "USD", "2": "GBP", "3": "EUR", "4": "TRY" };

export interface SiteContext {
  locale: SiteLocale | null;
  currency: Currency | null;
}

function inputValue(doc: Document | null | undefined, id: string): string | null {
  try {
    const el = doc?.getElementById(id);
    if (!el || !(el instanceof (doc as Document).defaultView!.HTMLInputElement || "value" in el)) return null;
    const value = (el as HTMLInputElement).value;
    return typeof value === "string" ? value.trim() : null;
  } catch {
    return null;
  }
}

export function readSiteContext(doc: Document | null | undefined): SiteContext {
  const dil = inputValue(doc, "hiddenDil")?.toLowerCase() ?? null;
  const doviz = inputValue(doc, "hiddenDoviz") ?? null;
  return {
    locale: dil && (SITE_LOCALES as string[]).includes(dil) ? (dil as SiteLocale) : null,
    currency: doviz && CURRENCY_BY_ID[doviz] ? CURRENCY_BY_ID[doviz] : null,
  };
}

/**
 * The host page's language, which is the widget's initial language (business rule).
 * Sources, in order: villamine.com's #hiddenDil (tr-tr / en-us / de-de), then the page's
 * <html lang> attribute. Browser language, IP or location are never used.
 */
export function readHostLanguage(doc: Document | null | undefined): string | null {
  const dil = inputValue(doc, "hiddenDil")?.toLowerCase() ?? null;
  if (dil && (SITE_LOCALES as string[]).includes(dil)) return dil.split("-")[0];
  try {
    const htmlLang = doc?.documentElement?.getAttribute("lang")?.trim().toLowerCase() ?? "";
    const base = htmlLang.split(/[-_]/)[0];
    if (/^[a-z]{2,3}$/.test(base)) return base;
  } catch {
    /* no document */
  }
  return null;
}

/** Maps a conversation language tag to a site locale when the site has one; otherwise the fallback. */
export function siteLocaleForLanguage(language: string | null | undefined, fallback: SiteLocale = "en-us"): SiteLocale {
  const base = (language ?? "").toLowerCase().split(/[-_]/)[0];
  if (base === "tr") return "tr-tr";
  if (base === "de") return "de-de";
  if (base === "en") return "en-us";
  return fallback;
}

/** The existing villa detail page on villamine.com for a slug and site locale. */
export function villaPageUrl(slug: string, locale: SiteLocale): string {
  const safeSlug = String(slug).replace(/[^a-z0-9-]/gi, "");
  return `${VILLAMINE_ORIGIN}/${locale}/${safeSlug}`;
}

/**
 * Link locale policy: the site's own locale wins (the visitor chose it); else
 * the conversation language if the site supports it; else English.
 */
export function linkLocale(siteLocale: SiteLocale | null, conversationLanguage: string | null): SiteLocale {
  return siteLocale ?? siteLocaleForLanguage(conversationLanguage, "en-us");
}

export interface HostContact {
  url: string;
  kind: "whatsapp" | "phone" | "link";
}

/**
 * The host page's own contact action (villamine.com renders WhatsApp and tel: links in its
 * header/footer). Read at mount so the widget reuses the site's configured number without
 * hardcoding anything; null when the page exposes none.
 */
export function readHostContact(doc: Document | null | undefined): HostContact | null {
  try {
    const wa = doc?.querySelector<HTMLAnchorElement>('a[href^="https://wa.me/"], a[href^="https://api.whatsapp.com/"], a[href^="whatsapp:"]');
    if (wa?.href) return { url: wa.href, kind: "whatsapp" };
    const tel = doc?.querySelector<HTMLAnchorElement>('a[href^="tel:"]');
    if (tel?.href) return { url: tel.href, kind: "phone" };
  } catch {
    /* no document */
  }
  return null;
}
